import React, { useEffect, useRef, useState, useCallback } from "react";
import TextCardImageLeft, {
  TextCardImageLeftProps,
} from "../ui/text-card-image-left";
import Arrow from "../ui/arrow";
import useEmblaCarousel from "embla-carousel-react";
import { LOCATIONS_SERVICES_DATA } from "@/lib/data";
import { cn } from "@/lib/utils";

export type LocationsServiceType = {
  serviceType: string;
  locationsServiceItems: TextCardImageLeftProps[];
};

export interface LocationsServicesProps {
  id?: string;
  className?: string;
  heading?: string;
  subheading?: string;
  description?: string | React.ReactNode;
  locationsServiceType: LocationsServiceType[];
}

const defaultProps: LocationsServicesProps = {
  heading: "",
  subheading: "",
  description: "",
  locationsServiceType: LOCATIONS_SERVICES_DATA,
};

const LocationsServices: React.FC<LocationsServicesProps> = ({
  id = "",
  className,
  heading,
  subheading,
  description,
  locationsServiceType,
}) => {
  heading = heading ?? defaultProps.heading;
  subheading = subheading ?? defaultProps.subheading;
  description = description ?? defaultProps.description;
  locationsServiceType =
    locationsServiceType ?? defaultProps.locationsServiceType;

  // State to track selected service type
  const [selectedServiceType, setSelectedServiceType] = useState("Families");

  // Find the selected service type data
  const selectedService = locationsServiceType.find(
    (type) => type.serviceType === selectedServiceType
  );

  const familiesButtonRef = useRef<HTMLButtonElement | null>(null);

  // Embla carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: 1,
    dragFree: true,
    breakpoints: {
      "(min-width: 744px)": { slidesToScroll: 2, dragFree: false },
      "(min-width: 1280px)": { slidesToScroll: 3, dragFree: false },
    },
  });

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  // Add useEffect to handle service type changes
  useEffect(() => {
    if (emblaApi) {
      emblaApi.scrollTo(0); // Reset to first slide when service type changes
    }
  }, [selectedServiceType, emblaApi]);

  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "mx-auto flex w-full max-w-lg flex-col gap-4 bg-white pt-4 md:gap-6 md:pb-2 md:pt-8 lg:gap-8 lg:px-3 lg:pb-3 lg:pt-10",
        className
      )}
    >
      <div className="flex flex-col gap-2 px-4 md:gap-3 md:px-6 lg:gap-4 lg:px-0">
        <h2 className="font-montserrat text-heading-4 text-secondary-500 md:text-heading-3 lg:text-heading-2">
          {heading}
        </h2>
        <h3 className="font-montserrat text-subheading-3-400 text-neutral-500 lg:text-subheading-2">
          {subheading}
        </h3>
      </div>
      {description &&
        (typeof description === "string"
          ? description.replace(/<[^>]*>/g, "").trim() // Strip HTML tags and check for content
          : description) && (
          <div className="flex flex-col px-4 md:px-6 lg:w-[726px] lg:px-0">
            <p className="font-roboto text-body-1-300 text-neutral-500">
              {typeof description === "string" ? (
                <div dangerouslySetInnerHTML={{ __html: description }} />
              ) : (
                description
              )}
            </p>
          </div>
        )}
      <div className="flex flex-col gap-2">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row px-4 md:px-6 lg:px-0">
            {locationsServiceType.map((type) => (
              <div key={type.serviceType}>
                <button
                  ref={
                    type.serviceType === "Families" ? familiesButtonRef : null
                  }
                  className={cn(
                    "w-fit cursor-pointer px-3 pb-[5px] pt-1 font-montserrat text-subheading-3-400 text-neutral-500 outline-none transition-all duration-300 ease-out active:bg-secondary-100",
                    type.serviceType === selectedServiceType
                      ? "border-b-2 border-secondary-600 pb-1 text-subheading-3-500 text-secondary-600"
                      : "hover:border-b hover:border-secondary-500 hover:text-secondary-500"
                  )}
                  onClick={() => setSelectedServiceType(type.serviceType)}
                >
                  {type.serviceType}
                </button>
              </div>
            ))}
          </div>
          <div className="hidden flex-row gap-4 lg:flex">
            <Arrow
              size="sm"
              direction="left"
              color="teal"
              buttonType="filled"
              onClick={scrollPrev}
              disabled={prevBtnDisabled}
              aria-label="Scroll to previous news card"
              aria-disabled={prevBtnDisabled}
            >
              Prev
            </Arrow>
            <Arrow
              size="sm"
              direction="right"
              color="teal"
              buttonType="filled"
              onClick={scrollNext}
              disabled={nextBtnDisabled}
              aria-label="Scroll to next news card"
              aria-disabled={nextBtnDisabled}
            >
              Next
            </Arrow>
          </div>
        </div>

        <div
          className="overflow-hidden"
          ref={emblaRef}
        >
          <div className="flex gap-4 px-4 md:gap-6 md:px-6 lg:px-0">
            {selectedService?.locationsServiceItems.map((item, index) => (
              <div
                key={index}
                className="h-full w-[300px] flex-shrink-0 py-4 first:pl-4 last:pr-4 md:w-[360px] md:py-6 md:last:pr-6 lg:py-7"
              >
                <TextCardImageLeft {...item} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsServices;
